import {
  CheckCircle,
  Copy,
  Edit,
  Key,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  XCircle,
  FileText,
  UserCheck,
  Users,
  GraduationCap,
  Mail,
  AppWindow,
  Clock,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { appAPI } from "../utils/api";
import Dialog from "../components/ui/dialog";
import useAuthStore from "../stores/authStore";

function randomBase62(len = 24) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let str = '';
  for (let i = 0; i < len; i++) {
    str += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return str;
}

const AppsPage = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    display_name: "",
    description: "",
    client_id: "",
    client_secret: "",
    redirect_uris: "",
    logo_url: "",
    is_active: true,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editForm, setEditForm] = useState({
    display_name: "",
    description: "",
    redirect_uris: "",
    logo_url: "",
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editFormErrors, setEditFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAppId, setDeleteAppId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenAppId, setRegenAppId] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState("");
  const [regenSecret, setRegenSecret] = useState("");

  const [copiedId, setCopiedId] = useState(null);
  const [copiedType, setCopiedType] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // アプリ一覧を取得
  const fetchApps = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await appAPI.getApps({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...params,
      });
      setApps(response.data.apps);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || "アプリ一覧の取得に失敗しました");
      console.error("アプリ一覧取得エラー:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  // 初回ロードと検索
  useEffect(() => {
    fetchApps();
  }, [searchTerm, fetchApps]);

  // 削除確認ダイアログを開く
  const handleDeleteApp = (appId) => {
    setDeleteAppId(appId);
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  // 削除を確定
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await appAPI.deleteApp(deleteAppId);
      setDeleteDialogOpen(false);
      fetchApps();
    } catch (err) {
      setDeleteError(err.response?.data?.error || "アプリケーションの削除に失敗しました");
    } finally {
      setDeleteLoading(false);
    }
  };

  // シークレット再生成確認ダイアログを開く
  const handleRegenerateSecret = (appId) => {
    setRegenAppId(appId);
    setRegenError("");
    setRegenSecret("");
    setRegenDialogOpen(true);
  };

  // シークレット再生成を確定
  const handleRegenConfirm = async () => {
    setRegenLoading(true);
    setRegenError("");
    try {
      const response = await appAPI.regenerateSecret(regenAppId);
      setRegenSecret(response.data.client_secret);
      fetchApps();
    } catch (err) {
      setRegenError(err.response?.data?.error || "シークレット再生成に失敗しました");
    } finally {
      setRegenLoading(false);
    }
  };

  const validateCreateForm = (form = createForm) => {
    const errors = {};
    if (!form.name || form.name.length < 2 || form.name.length > 50) {
      errors.name = "アプリ名の長さは2～50文字でなければなりません";
    }
    if (!form.display_name) {
      errors.display_name = "表示名を入力してください";
    }
    if (form.description && form.description.length > 500) {
      errors.description = "説明は500文字以内で入力してください";
    }
    if (!form.redirect_uris || form.redirect_uris.trim() === "") {
      errors.redirect_uris = "リダイレクトURIを入力してください";
    }
    return errors;
  };

  // 新規作成ダイアログを開く
  const handleOpenCreate = () => {
    setCreateForm({
      name: "",
      display_name: "",
      description: "",
      client_id: randomBase62(24),
      client_secret: randomBase62(32),
      redirect_uris: "",
      logo_url: "",
      is_active: true,
    });
    setCreateFormErrors({});
    setCreateError("");
    setCreateDialogOpen(true);
  };

  // 新規作成フォームの変更
  const handleCreateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newForm = {
      ...createForm,
      [name]: type === "checkbox" ? checked : value,
    };
    setCreateForm(newForm);
    setCreateFormErrors(validateCreateForm(newForm));
  };

  // 新規作成を保存
  const handleCreateSave = async () => {
    const errors = validateCreateForm();
    setCreateFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setCreateLoading(true);
    setCreateError("");
    try {
      // redirect_uris: 改行をカンマに置換し、余分な空白やカンマを除去
      const redirect_uris = createForm.redirect_uris
        .replace(/\n+/g, ',')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .join(',');
      const payload = {
        ...createForm,
        redirect_uris,
      };
      await appAPI.createApp(payload);
      setCreateDialogOpen(false);
      fetchApps();
    } catch (err) {
      setCreateError(err.response?.data?.error || "アプリケーションの作成に失敗しました");
    } finally {
      setCreateLoading(false);
    }
  };

  const validateEditForm = (form = editForm) => {
    const errors = {};
    if (!form.display_name) {
      errors.display_name = "表示名を入力してください";
    }
    if (form.description && form.description.length > 500) {
      errors.description = "説明は500文字以内で入力してください";
    }
    if (!form.redirect_uris || form.redirect_uris.trim() === "") {
      errors.redirect_uris = "リダイレクトURIを入力してください";
    }
    return errors;
  };

  // 編集ダイアログを開く
  const handleEditApp = (app) => {
    setEditApp(app);
    setEditForm({
      display_name: app.display_name || "",
      description: app.description || "",
      redirect_uris: Array.isArray(app.redirect_uris) ? app.redirect_uris.join("\n") : (app.redirect_uris || ""),
      logo_url: app.logo_url || "",
      is_active: !!app.is_active,
    });
    setEditFormErrors({});
    setEditError("");
    setEditDialogOpen(true);
  };

  // 編集フォームの変更
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newForm = {
      ...editForm,
      [name]: type === "checkbox" ? checked : value,
    };
    setEditForm(newForm);
    setEditFormErrors(validateEditForm(newForm));
  };

  // 編集を保存
  const handleEditSave = async () => {
    const errors = validateEditForm();
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setEditLoading(true);
    setEditError("");
    try {
      const redirect_uris = editForm.redirect_uris
        .replace(/\n+/g, ',')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .join(',');
      const payload = {
        display_name: editForm.display_name,
        description: editForm.description,
        redirect_uris,
        logo_url: editForm.logo_url,
        is_active: editForm.is_active,
      };
      await appAPI.updateApp(editApp.id, payload);
      setEditDialogOpen(false);
      fetchApps();
    } catch (err) {
      setEditError(err.response?.data?.error || "アプリケーションの更新に失敗しました");
    } finally {
      setEditLoading(false);
    }
  };

  const filteredApps = apps.filter(
    (app) =>
      app.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        アクティブ
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <XCircle className="w-3 h-3 mr-1" />
        非アクティブ
      </span>
    );
  };

  const copyToClipboard = (text, id, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setCopiedType(type);
    setTimeout(() => {
      setCopiedId(null);
      setCopiedType(null);
    }, 1000);
  };

  // アプリケーション名に基づいてアイコンを取得
  const getAppIcon = (appName) => {
    const iconMap = {
      resume: FileText,
      evaluation: UserCheck,
      interview: Users,
      project: Clock,
      elearning: GraduationCap,
      webmail: Mail,
    };
    return iconMap[appName] || AppWindow;
  };

  // アプリケーション名に基づいて色を取得
  const getAppColor = (appName) => {
    const colorMap = {
      resume: "bg-blue-500",
      evaluation: "bg-green-500",
      interview: "bg-purple-500",
      project: "bg-orange-500",
      elearning: "bg-indigo-500",
      webmail: "bg-red-500",
    };
    return colorMap[appName] || "bg-gray-500";
  };

  // 新規作成・再生成ダイアログ内でclient_secretをコピー
  const handleCopySecret = (secret) => {
    if (!secret) return;
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 1000);
  };

  if (loading && apps.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            アプリケーション一覧を読み込み中...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => fetchApps()}>再試行</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            アプリケーション管理
          </h1>
          <p className="text-muted-foreground">
            SSOシステムに登録されているアプリケーションの管理
          </p>
        </div>
        <Button className="flex items-center space-x-2" onClick={handleOpenCreate} disabled={currentUser?.role !== "admin"}>
          <Plus className="h-4 w-4" />
          <span>新規アプリケーション</span>
        </Button>
      </div>

      {/* 検索とフィルター */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="アプリケーション名、説明で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline">フィルター</Button>
          </div>
        </CardContent>
      </Card>

      {/* アプリケーション一覧 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredApps.map((app) => (
          <Card key={app.id} className="app-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getAppColor(app.name)}`}>
                    {React.createElement(getAppIcon(app.name), { className: "h-5 w-5 text-white" })}
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {app.display_name}
                    </CardTitle>
                    <CardDescription>{app.name}</CardDescription>
                  </div>
                </div>
                {getStatusBadge(app.is_active)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{app.description}</p>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    クライアントID:
                  </span>
                  <div className="flex items-center space-x-1">
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {app.client_id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(app.client_id, app.id, 'client_id')}
                    >
                      {copiedId === app.id && copiedType === 'client_id' ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    クライアントシークレット:
                  </span>
                  <div className="flex items-center space-x-1">
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {"非表示"}
                    </code>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-muted-foreground">
                  登録日: {new Date(app.created_at).toLocaleDateString("ja-JP")}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditApp(app)} disabled={currentUser?.role !== "admin"}>
                      <Edit className="mr-2 h-4 w-4" />
                      <span>編集</span>
                    </DropdownMenuItem>
                    {app.name === "webmail" && (
                      <DropdownMenuItem
                        onClick={() => navigate("/apps/webmail/mailboxes")}
                        disabled={currentUser?.role !== "admin"}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        <span>メールユーザー管理</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleRegenerateSecret(app.id)}
                      disabled={currentUser?.role !== "admin"}
                    >
                      <Key className="mr-2 h-4 w-4" />
                      <span>シークレット再生成</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDeleteApp(app.id)}
                      disabled={currentUser?.role !== "admin"}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>削除</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ページネーション */}
      {pagination.pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {pagination.total} 件中{" "}
                {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                件を表示
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchApps({ page: pagination.page - 1 })}
                >
                  前へ
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => fetchApps({ page: pagination.page + 1 })}
                >
                  次へ
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 統計情報 */}
      <Card>
        <CardHeader>
          <CardTitle>統計情報</CardTitle>
          <CardDescription>アプリケーションの利用状況と統計</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{pagination.total}</div>
              <div className="text-sm text-muted-foreground">
                総アプリケーション数
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {apps.filter((app) => app.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground">アクティブ</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {apps.filter((app) => !app.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground">非アクティブ</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">-</div>
              <div className="text-sm text-muted-foreground">総ユーザー数</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {createDialogOpen && (
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          title="新規アプリケーション作成"
          footer={
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
                キャンセル
              </Button>
              <Button onClick={handleCreateSave} disabled={createLoading || Object.keys(createFormErrors).length > 0 || currentUser?.role !== "admin"}>
                {createLoading ? "作成中..." : "作成"}
              </Button>
            </div>
          }
        >
          {createError && <div className="text-red-600 mb-2">{createError}</div>}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">アプリ名</label>
              <Input name="name" value={createForm.name} onChange={handleCreateFormChange} />
              {createFormErrors.name && <div className="text-red-600 text-xs">{createFormErrors.name}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">表示名</label>
              <Input name="display_name" value={createForm.display_name} onChange={handleCreateFormChange} />
              {createFormErrors.display_name && <div className="text-red-600 text-xs">{createFormErrors.display_name}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">説明</label>
              <Input name="description" value={createForm.description} onChange={handleCreateFormChange} />
              {createFormErrors.description && <div className="text-red-600 text-xs">{createFormErrors.description}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">クライアントID</label>
              <Input name="client_id" value={createForm.client_id} readOnly />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">クライアントシークレット</label>
              <div className="flex items-center space-x-2">
                <Input name="client_secret" value={createForm.client_secret} readOnly />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleCopySecret(createForm.client_secret)}
                >
                  {copiedSecret ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">リダイレクトURI（改行またはカンマ区切り）</label>
              <textarea name="redirect_uris" value={createForm.redirect_uris} onChange={handleCreateFormChange} rows={3} className="w-full border rounded px-2 py-1" />
              {createFormErrors.redirect_uris && <div className="text-red-600 text-xs">{createFormErrors.redirect_uris}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ロゴURL</label>
              <Input name="logo_url" value={createForm.logo_url} onChange={handleCreateFormChange} />
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" name="is_active" checked={createForm.is_active} onChange={handleCreateFormChange} id="create_is_active" />
              <label htmlFor="create_is_active" className="text-sm">アクティブ</label>
            </div>
          </div>
        </Dialog>
      )}

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="アプリケーション編集"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              キャンセル
            </Button>
            <Button onClick={handleEditSave} disabled={editLoading || Object.keys(editFormErrors).length > 0 || currentUser?.role !== "admin"}>
              {editLoading ? "保存中..." : "保存"}
            </Button>
          </div>
        }
      >
        {editError && <div className="text-red-600 mb-2">{editError}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">表示名</label>
            <Input name="display_name" value={editForm.display_name} onChange={handleEditFormChange} />
            {editFormErrors.display_name && <div className="text-red-600 text-xs">{editFormErrors.display_name}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">説明</label>
            <Input name="description" value={editForm.description} onChange={handleEditFormChange} />
            {editFormErrors.description && <div className="text-red-600 text-xs">{editFormErrors.description}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">リダイレクトURI（改行またはカンマ区切り）</label>
            <textarea name="redirect_uris" value={editForm.redirect_uris} onChange={handleEditFormChange} rows={3} className="w-full border rounded px-2 py-1" />
            {editFormErrors.redirect_uris && <div className="text-red-600 text-xs">{editFormErrors.redirect_uris}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ロゴURL</label>
            <Input name="logo_url" value={editForm.logo_url} onChange={handleEditFormChange} />
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" name="is_active" checked={editForm.is_active} onChange={handleEditFormChange} id="edit_is_active" />
            <label htmlFor="edit_is_active" className="text-sm">アクティブ</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="アプリケーション削除の確認"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
              キャンセル
            </Button>
            <Button onClick={handleDeleteConfirm} disabled={deleteLoading} variant="destructive">
              {deleteLoading ? "削除中..." : "削除"}
            </Button>
          </div>
        }
      >
        {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
        <div>本当にこのアプリケーションを削除しますか？この操作は取り消せません。</div>
      </Dialog>

      <Dialog
        open={regenDialogOpen}
        onClose={() => setRegenDialogOpen(false)}
        title="クライアントシークレット再生成の確認"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setRegenDialogOpen(false)} disabled={regenLoading}>
              キャンセル
            </Button>
            <Button onClick={handleRegenConfirm} disabled={regenLoading || currentUser?.role !== "admin"}>
              {regenLoading ? "再生成中..." : "再生成"}
            </Button>
          </div>
        }
      >
        {regenError && <div className="text-red-600 mb-2">{regenError}</div>}
        {regenSecret ? (
          <div className="flex items-center space-x-2 text-green-700 font-bold">
            <span>新しいクライアントシークレット: {regenSecret}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleCopySecret(regenSecret)}
            >
              {copiedSecret ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        ) : (
          <div>本当にクライアントシークレットを再生成しますか？<br/>再生成後は新しい値を必ず保存してください。</div>
        )}
      </Dialog>
    </div>
  );
};

export default AppsPage;
